// The game's screens over the battlefield (2026-10-25, ui branch): the title, the hero pick (2026-10-26), the army pick,
// pause with settings, and the end card. flow.ts says where each press leads; this file draws the screen and tells main.ts through `hooks`.
// The title (2026-10-26, ui2) is the logo cut-out (assets/ui/logo.webp) standing over the attract match, which fills the
// screen, with the menu beside it; the logo settles onto the table as it opens (still under reduced motion). It replaced
// the toy-box lid, whose framed logo loop left half the card empty.
// Every other card (2026-10-26, uiart) is set down in a felt-lined toy box (the .screens backdrop) with its title on a
// navy ribbon, portraits in gold medallions and a badge beside each skill and setting. The end card opens on a picture
// of the table (a trophy, or a toppled fort) and its ribbon unrolls.
// ?qa=1 mirrors the screen on window.__qa.ui (screen, panel, mode, the picked army, the end card's words and rows).
import { FACTION, FactionId, HERO, HeroId } from '../../sim/defs';
import type { Ev, Sim } from '../../sim/sim';
import { CARD_SIZES, POSTER_SIZES, WIDE_CARD, bannerSrcset, iconPic, posterSrc, posterSrcset, setPic, unitPic, type CardId, type Pic } from './art';
import { ARMY_CARDS, HERO_CARDS, TEAM, endRows, endWords, mmss } from './data';
import { Action, Screen, next } from './flow';
import { ARMY_KEY, CAMERAS, CameraDist, QUALITIES, Quality, SPEEDS, Settings, Speed } from './settings';
import type { LobbyView } from '../online';

export type Mode = 'play' | 'watch';
type Hooks = {
  replay: () => void;
  /** Start a match: `fresh` false keeps the attract match running (Watch from the title). */
  start: (mode: Mode, fresh: boolean) => void;
  pick: (f: FactionId) => void;
  pickHero: (h: HeroId) => void;
  toTitle: () => void;
  /** A setting changed; `key` is the one the player pressed (so a ?speed= survives a camera change, Astra). */
  settings: (s: Settings, key: keyof Settings) => void;
  /** Screen changed (main.ts shows or hides the HUD and runs or stops the clock). */
  changed: (s: Screen) => void;
  /** Opening the lobby (from the title or an end card): main.ts leaves any room and runs the attract match behind it. */
  toLobby: () => void;
  /** Online play (main.ts over client/online.ts). */
  net: {
    view: () => LobbyView;
    /** In an online match now (its pause and end cards say so). */
    online: () => boolean;
    quick: () => void; create: () => void; join: (code: string) => void;
    hero: (id: HeroId) => void; begin: () => void;
    /** Leave the room (or stop looking) and show the lobby's first card again. */
    cancel: () => void;
    link: () => string;
  };
};

const h = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = '') => {
  const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e;
};
const btn = (label: string, cls: string, fn: () => void) => { const b = h('button', cls, label); b.addEventListener('click', fn); return b; };
/** A strip picture, decorative (the words beside it say the same). */
const pic = (p: Pic, cls = '') => { const e = h('i', 'pic' + (cls ? ' ' + cls : '')); e.setAttribute('aria-hidden', 'true'); setPic(e, p); return e; };
/** The pick card's words fit it whole, no scrolling (Devin, 2026-10-31): the largest type size from 16 px down to the
 *  12.5 px floor, in quarter pixels, at which the card's body holds them (--k, which every size in index.html's art card
 *  block is an em of). A binary search, four or five layouts a pick. Off the page (no height yet) it waits for the card's
 *  ResizeObserver; data-k shows the size it chose (the UI walk reports it). If the floor still overflows (a reader's own larger text), the body scrolls, and the UI walk fails it. */
export const KIT_MAX = 16, KIT_MIN = 12.5;
const fitWords = (body: HTMLElement, text: HTMLElement) => {
  if (!body.clientHeight) return;
  const fits = (step: number) => { text.dataset.k = String(KIT_MIN + step / 4); text.style.setProperty('--k', `${KIT_MIN + step / 4}px`); return body.scrollHeight <= body.clientHeight; };
  let lo = 0, hi = (KIT_MAX - KIT_MIN) * 4;
  if (fits(hi)) return;
  while (lo < hi - 1) { const mid = (lo + hi) >> 1; if (fits(mid)) lo = mid; else hi = mid; }
  fits(lo);
};
/** A card's title on its ribbon. */
const ribbon = (text: string, cls = '') => h('h2', 'title ribbon' + (cls ? ' ' + cls : ''), text);
/** The hero you last led with, so the pick opens on a choice (2026-10-30 clarity pass): the kit is full from the start,
 *  nothing jumps when you pick, and the main button is live. The first hero on a first visit. */
/** A lobby corner standing up when someone takes it (index.html .corner.arrive runs this long). */
const ARRIVE_MS = 550;
const HERO_KEY = 'footmen-wars.hero';
const savedHero = (): HeroId | null => {
  try { const v = localStorage.getItem(HERO_KEY); if (v && HERO_CARDS.some((c) => c.id === v)) return v as HeroId; } catch { /* no storage */ }
  return null;
};
const lastHero = (): HeroId => savedHero() ?? HERO_CARDS[0].id;
/** The army you last marched with, for the same reason (main.ts saves it); the first army on a first visit. */
const lastArmy = (): FactionId => {
  try { const v = localStorage.getItem(ARMY_KEY); if (v && ARMY_CARDS.some((c) => c.id === v)) return v as FactionId; } catch { /* no storage */ }
  return ARMY_CARDS[0].id;
};
/** A hero's or an army's poster: decorative (the tile's name and label say who it is). The title warms the heroes' in
 *  idle time, so the pick opens with every poster there and nothing on the title's own load waits for them. */
const poster = (id: CardId) => {
  const img = h('img', 'poster');
  img.sizes = POSTER_SIZES; img.srcset = posterSrcset(id); img.src = posterSrc(id, 240);
  img.width = 240; img.height = 360; img.alt = ''; img.decoding = 'async'; img.draggable = false;
  return img;
};
/** The picked card's picture (2026-10-31, art cards): the poster where the card is tall, a hero's wide banner where it is
 *  wide (an army has only its poster, cropped). Decorative: the card's words name it. */
const cardPicture = (id: CardId, banner: boolean) => {
  const pic = h('picture', 'card-art');
  if (banner) { const w = h('source'); w.media = WIDE_CARD; w.srcset = bannerSrcset(id as HeroId); w.sizes = CARD_SIZES; pic.append(w); }
  const img = h('img'); img.sizes = CARD_SIZES; img.srcset = posterSrcset(id); img.src = posterSrc(id, 480);
  img.alt = ''; img.decoding = 'async'; img.draggable = false; pic.append(img);
  return pic;
};
// Once a page: the warm-up is skipped if the pick opened first, and each image is held only until it lands in the cache
// (held, so it is not collected before; let go after, so a later resize never refetches through it). The title runs a
// live match every frame, so idle time may never come: the timeout is what usually fires.
let warmed = false;
const warm = new Set<HTMLImageElement>();
const warmPosters = () => {
  if (warmed) return;
  const go = () => {
    if (warmed) return; warmed = true;
    for (const c of HERO_CARDS) { const img = poster(c.id); img.onload = img.onerror = () => warm.delete(img); warm.add(img); }
  };
  if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 1500 }); else setTimeout(go, 1500);
};
const TICK = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.2 4.2L19 7" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const sym = (name: string) => { const e = h('i', 'sym'); e.dataset.sym = name; e.setAttribute('aria-hidden', 'true'); return e; };
const PLAY_ICON = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M7 4.5v15a1 1 0 0 0 1.5.86l12-7.5a1 1 0 0 0 0-1.72l-12-7.5A1 1 0 0 0 7 4.5z" fill="currentColor"/></svg>';
const GEAR_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M10.3 2h3.4l.5 2.6 1.7.8 2.2-1.5 2.4 2.4-1.5 2.2.8 1.7 2.6.5v3.4l-2.6.5-.8 1.7 1.5 2.2-2.4 2.4-2.2-1.5-1.7.8-.5 2.6h-3.4l-.5-2.6-1.7-.8-2.2 1.5-2.4-2.4 1.5-2.2-.8-1.7L2 13.7v-3.4l2.6-.5.8-1.7-1.5-2.2 2.4-2.4 2.2 1.5 1.7-.8zM12 8.6a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8z" fill="currentColor" fill-rule="evenodd"/></svg>';

const SPEED_LABEL: Record<Speed, string> = { 1: 'Normal', 1.5: 'Fast', 2: 'Faster' };
const QUALITY_LABEL: Record<Quality, string> = { best: 'Best', fast: 'Light' };
const CAMERA_LABEL: Record<CameraDist, string> = { close: 'Close', normal: 'Normal', far: 'Far' };

export class Screens {
  screen: Screen = 'play';
  mode: Mode = 'play';
  private panel: 'main' | 'settings' = 'main';
  private picked: FactionId | null = null;
  private hero: HeroId | null = null;
  private fellAt: (number | null)[] = [null, null, null, null];
  private el = h('div', 'screens');
  private returnFocus: HTMLElement | null = null;

  constructor(root: HTMLElement, readonly hooks: Hooks, readonly sim: () => Sim, readonly me: () => number, public settings: Settings, readonly qa: boolean) {
    root.append(this.el);
    addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' && e.key.toLowerCase() !== 'p') return;
      // a jump catching up has only its cover on screen (main.ts; Flash round 2 F2)
      if (document.documentElement.dataset.jump === 'catching-up') return;
      if (this.screen === 'play') this.go('pause');
      else if (this.screen === 'pause') this.go('resume');
      else if (this.screen === 'pick' && e.key === 'Escape') this.go('cancel');
      else if (this.screen === 'title' && this.panel === 'settings' && e.key === 'Escape') { this.panel = 'main'; this.draw(); }
      else if (this.screen === 'lobby' && this.lobbyPanel === 'hero' && e.key === 'Escape') { this.lobbyPanel = 'room'; this.draw(); }
    });
  }

  /** Move by `a` if the current screen allows it. Returns whether the screen changed. */
  go(a: Action): boolean {
    const was = this.screen, to = next(was, a);
    if (to === was && !(a === 'end' && was === 'end')) return false; // a second 'end' redraws (the winner after your fall)
    if (a === 'play' || a === 'watch' || a === 'again') {
      const mode: Mode = a === 'again' ? this.mode : a;
      // Watch from the title keeps the attract match; Watch after your fall keeps yours; anything else starts afresh
      const fresh = !(a === 'watch' && (was === 'title' || was === 'end'));
      this.mode = mode;
      if (fresh) { this.fellAt = [null, null, null, null]; this.picked = null; this.hero = lastHero(); }
      this.hooks.start(mode, fresh);
    }
    if (a === 'title') { this.fellAt = [null, null, null, null]; this.hooks.toTitle(); }
    if (a === 'online') { this.lobbyPanel = 'room'; this.heroSent = false; this.hooks.toLobby(); }
    if (a === 'begin') { this.mode = 'play'; this.fellAt = [null, null, null, null]; this.picked = null; }
    // a match opened by an address skip (?seed=, ?army=, ?qa=1) never ran go('play'): the hero pick still opens on a hero (Flash F3)
    if (a === 'hero') this.hero ??= lastHero();
    if (a === 'pick') { this.picked = lastArmy(); this.returnFocus = document.activeElement as HTMLElement | null; }
    this.screen = to; this.panel = 'main';
    // main.ts makes the HUD live again before draw() hands focus back to it (Astra: inert refused the focus)
    this.hooks.changed(to);
    this.draw();
    return true;
  }
  /** What the open pick has on (main.ts plays its army's theme: audio-logic choosingArmy). */
  get choice() { return { army: this.picked, hero: this.hero }; }
  /** Show the title without a move (the first load). */
  title() { this.screen = 'title'; this.mode = 'watch'; this.panel = 'main'; this.draw(); this.hooks.changed('title'); warmPosters(); }
  /** Straight into a match (a skip param in the address). */
  playing(mode: Mode, fresh = false) { if (fresh) this.fellAt = [null, null, null, null]; this.screen = 'play'; this.mode = mode; this.draw(); this.hooks.changed('play'); }

  onEvent(e: Ev) {
    const s = this.sim();
    if (e.e === 'out') {
      this.fellAt[e.p] = s.time;
      if (e.p === this.me() && this.mode === 'play' && s.winner < 0 && this.screen !== 'title') this.go('end');
    }
    if (e.e === 'win' && this.screen !== 'title') this.go('end');
  }

  private draw() {
    this.el.dataset.screen = this.screen; this.el.dataset.panel = this.panel;
    clearInterval(this.countdown);
    let card: HTMLElement | null = null;
    if (this.screen === 'title') card = this.panel === 'settings' ? this.settingsCard(() => { this.panel = 'main'; this.draw(); }) : this.titleCard();
    else if (this.screen === 'lobby') card = this.lobbyCard();
    else if (this.screen === 'hero') card = this.heroCard();
    else if (this.screen === 'pick') card = this.pickCard();
    else if (this.screen === 'pause') card = this.panel === 'settings' ? this.settingsCard(() => { this.panel = 'main'; this.draw(); }) : this.pauseCard();
    else if (this.screen === 'end') card = this.endCard();
    if (card && this.screen !== 'title') { card.setAttribute('role', 'dialog'); card.setAttribute('aria-modal', 'true'); }
    this.el.replaceChildren(...(card ? [card] : []));
    this.el.hidden = !card;
    if (card) (card.querySelector<HTMLElement>('[data-first]') ?? card.querySelector<HTMLElement>('button'))?.focus({ preventScroll: true });
    else if (this.returnFocus?.isConnected) { this.returnFocus.focus({ preventScroll: true }); this.returnFocus = null; }
    this.publish();
  }

  // ---- title: the logo stands on the table over the attract match, the menu beside it (below it on a portrait phone)
  private titleCard() {
    const wrap = h('section', 'title-screen'); wrap.setAttribute('aria-label', 'Footmen Wars Online');
    const logo = h('img', 'logo'); logo.src = './ui/logo.webp'; logo.alt = 'Footmen Wars Online'; logo.width = 1008; logo.height = 1020;
    logo.decoding = 'async';
    const heading = h('h1', 'title-h1'); heading.append(logo); // the screen's one heading, for screen readers jumping by heading
    const menu = h('div', 'menu card');
    // online first (2026-11-01, Devin: "make this online"); bots stay one tap away and need no connection
    const play = btn('', 'plaque big primary hero-btn', () => this.go('online')); play.dataset.first = '';
    play.innerHTML = PLAY_ICON; play.append(h('span', '', 'Play online'));
    const settings = btn('', 'plaque small-btn', () => { this.panel = 'settings'; this.draw(); });
    settings.innerHTML = GEAR_ICON; settings.append(h('span', '', 'Settings'));
    menu.append(play, btn('Play vs bots', 'plaque big', () => this.go('play')), btn('Watch a match', 'plaque big', () => this.go('watch')), settings, h('p', 'maker', 'A tinwhistlegames game'));
    wrap.append(heading, menu);
    return wrap;
  }

  // ---- settings (from the title or the pause card). 2026-10-30 clarity pass (/taste clarity.md, Devin: "cluttered"):
  // three groups by spacing, an on/off is one switch (was two plaques), a three-way pick is one segmented control with a
  // quiet raised selection, so Done is the only filled button. Depth blur shows only with Best graphics, where it works.
  // A press updates the card in place (2026-10-31, tinwhistlegames review F6): redrawing it on every press sent focus
  // back to Done, replayed the card's drop-in and replaced the switch before its slide could run.
  private settingsCard(back: () => void) {
    const card = h('section', 'card settings'); card.setAttribute('aria-label', 'Settings');
    card.append(ribbon('Settings'));
    const s = this.settings;
    /** Each control's state from the settings, run after every press (the hook may change more than the key pressed). */
    const syncs: (() => void)[] = [];
    const sync = () => syncs.forEach((f) => f());
    const save = (key: keyof Settings) => { this.hooks.settings(this.settings, key); sync(); this.publish(); };
    const label = (text: string, hint: string) => {
      const lab = h('span', 'set-label'); lab.append(h('b', '', text));
      if (hint) lab.append(h('small', '', hint));
      return lab;
    };
    type OnOff = 'tips' | 'sound' | 'music' | 'blur' | 'weather';
    const toggle = (key: OnOff, text: string, hint = '') => {
      const r = btn('', 'set-row set-switch', () => { s[key] = s[key] === 'on' ? 'off' : 'on'; save(key); });
      r.setAttribute('role', 'switch');
      syncs.push(() => r.setAttribute('aria-checked', String(s[key] === 'on')));
      r.append(pic(iconPic(key)), label(text, hint), h('span', 'switch'));
      return r;
    };
    const choice = <T extends string | number>(key: keyof Settings, text: string, hint: string, opts: readonly T[], names: Record<string, string>, set: (v: T) => void) => {
      const r = h('div', 'set-row set-pick'), seg = h('div', 'seg');
      seg.setAttribute('role', 'group'); seg.setAttribute('aria-label', text);
      for (const o of opts) {
        const b = btn(names[String(o)], '', () => { set(o); save(key); });
        syncs.push(() => b.setAttribute('aria-pressed', String(s[key] === o))); seg.append(b);
      }
      r.append(pic(iconPic(key === 'quality' ? 'graphics' : key)), label(text, hint), seg); return r;
    };
    const group = (title: string, ...rows: HTMLElement[]) => {
      const g = h('section', 'set-group'); g.setAttribute('aria-label', title);
      g.append(h('h3', 'set-head', title), ...rows); return g;
    };
    const blur = toggle('blur', 'Depth blur', 'Soft distant scenery');
    syncs.push(() => { blur.hidden = s.quality !== 'best'; });
    const groups = h('div', 'set-groups');
    groups.append(
      group('Audio', toggle('sound', 'Sound'), toggle('music', 'Music')),
      group('Game',
        toggle('tips', 'Tips', 'Help while you play'),
        choice('speed', 'Game speed', 'How fast the match clock runs', SPEEDS, SPEED_LABEL, (v) => (s.speed = v)),
        choice('camera', 'Camera', '', CAMERAS, CAMERA_LABEL, (v) => (s.camera = v))),
      group('Look',
        choice('quality', 'Graphics', 'Light runs better on older phones', QUALITIES, QUALITY_LABEL, (v) => (s.quality = v)),
        blur,
        toggle('weather', 'Weather', 'Rain, snow and leaves')),
    );
    sync();
    const done = btn('Done', 'plaque big primary', back); done.dataset.first = '';
    const foot = h('div', 'actions'); foot.append(done); card.append(groups, foot);
    return card;
  }

  // ---- the two picks share one art card (2026-10-31, hero cards, then the army cards with Devin's "as beautiful an image
  // as possible ... bespoke"): eight poster tiles, and beside them (under them on a portrait screen) the picked one's
  // card, which is that picture big with its words on a navy scrim rising from its foot (the scrim grows with the words,
  // so the art stays clear above them; no panel over the art). The card's size comes from the layout, never from its
  // words (they fit it whole at every size, no scrolling: index.html's art card block), so a pick moves nothing. Picking crossfades the card's picture: the one authored
  // moment here, opacity only (a pick happens tens of times a day), and none under reduced motion.
  private artPick<T extends CardId>(o: {
    kind: 'hero' | 'army'; title: string; sub: string; label: string; items: { id: T; short: string; role: string; aria: string }[];
    picked: () => T | null; pick: (id: T) => void; words: (id: T) => HTMLElement[]; go: HTMLButtonElement; extra?: HTMLElement;
  }) {
    const me = Math.max(0, this.me()), plural = o.kind === 'hero' ? 'heroes' : 'armies';
    const card = h('section', `card pick art-pick ${plural}-card`); card.style.setProperty('--team', TEAM[me]); card.setAttribute('aria-label', o.title);
    const head = h('div', 'pick-head');
    head.append(ribbon(o.title), h('p', 'sub', o.sub));
    const grid = h('div', `tiles ${plural}`); grid.setAttribute('role', 'radiogroup'); grid.setAttribute('aria-label', o.label);
    const kit = h('div', `pick-kit ${o.kind}-kit`), body = h('div', 'kit-body'); kit.setAttribute('aria-live', 'polite');
    let art: HTMLElement | null = null;
    const show = (first: boolean) => {
      const id = o.picked(); if (!id) return;
      const next = cardPicture(id, o.kind === 'hero');
      if (!first) next.classList.add('fade-in');
      art ? art.replaceWith(next) : kit.prepend(next); art = next;
      const text = h('div', 'kit-text'); text.append(...o.words(id));
      body.replaceChildren(text); body.scrollTop = 0; fitWords(body, text);
    };
    kit.append(body);
    // the card's box changes with the window (a phone turned over): fit its words again. Only the words change size, never
    // the card (the layout sizes it), so this never feeds itself
    const refit = () => { const t = body.firstElementChild as HTMLElement | null; if (t) fitWords(body, t); };
    new ResizeObserver(refit).observe(kit);
    // a face that loads after the card shows changes the words' size but not the card's, so the observer misses it (Flash)
    const fonts = () => (kit.isConnected ? refit() : document.fonts.removeEventListener('loadingdone', fonts));
    document.fonts?.addEventListener('loadingdone', fonts);
    for (const c of o.items) {
      const b = h('button', `tile ${o.kind}`); b.setAttribute('role', 'radio'); b.dataset[o.kind] = c.id;
      const named = h('span', 'words'), tick = h('i', 'tick');
      tick.innerHTML = TICK; named.append(h('b', 'name', c.short), h('span', 'role', c.role));
      b.append(poster(c.id), tick, named);
      b.setAttribute('aria-checked', String(o.picked() === c.id));
      b.setAttribute('aria-label', c.aria);
      b.addEventListener('click', () => {
        if (o.picked() === c.id) return;
        o.pick(c.id);
        grid.querySelectorAll('.tile').forEach((x) => x.setAttribute('aria-checked', String((x as HTMLElement).dataset[o.kind] === c.id)));
        show(false); this.publish();
      });
      b.addEventListener('dblclick', () => o.go.click());
      grid.append(b);
    }
    show(true);
    const foot = h('div', 'actions'), side = h('div', 'pick-side');
    if (o.extra) foot.append(o.extra);
    foot.append(o.go); side.append(kit, foot);
    card.append(head, grid, side);
    // focus lands on the chosen one, so the focus ring and the choice are the same card
    ((grid.querySelector('.tile[aria-checked="true"]') ?? grid.firstElementChild) as HTMLElement).dataset.first = '';
    return card;
  }

  // ---- the hero pick, as a match you play starts (the clock waits); no Not yet, a match needs your hero. Eight heroes
  // (2026-10-26); the card under the picture: name, role, then the passive and skills.
  private heroCard() {
    warmed = true;
    const go = btn('', 'plaque big primary march', () => {
      if (!this.hero) return;
      try { localStorage.setItem(HERO_KEY, this.hero); } catch { /* the pick still holds for this match */ }
      this.hooks.pickHero(this.hero); this.go('heroPicked');
    });
    const say = () => { go.textContent = `Lead with ${HERO[this.hero ?? HERO_CARDS[0].id].short}`; };
    say();
    return this.artPick<HeroId>({
      kind: 'hero', title: 'Choose your hero', sub: 'Lead any army. Grow stronger. Return after falling.', label: 'Heroes',
      items: HERO_CARDS.map((c) => ({ id: c.id, short: c.short, role: c.role, aria: `${c.name}, ${c.role}` })),
      picked: () => this.hero, pick: (id) => { this.hero = id; say(); }, go,
      words: (id) => this.heroWords(id),
    });
  }

  /** A hero's words on its pick card: name, role, then each ability. */
  private heroWords(id: HeroId) {
    const c = HERO_CARDS.find((x) => x.id === id)!;
    const named = h('span', 'kit-head'); named.append(h('b', 'name', c.name), h('small', 'role', c.role));
    const list = h('span', 'kit');
    for (const l of c.lines) {
      // one row an ability: its icon and name with when it opens (a tag), then what it does, in full
      const row = h('span', 'kit-row'), line = h('span', 'kit-line');
      line.append(pic(iconPic(l.name)), h('b', '', l.name), h('small', 'when', l.when));
      row.append(line, h('small', 'detail', l.text)); list.append(row);
    }
    return [named, list];
  }

  // ---- the army pick, at your first tier-up (the clock stops while it is open); the card under the picture: the army,
  // its trait and what the trait does.
  private pickCard() {
    const go = btn('', 'plaque big primary march', () => {
      if (!this.picked) return;
      this.hooks.pick(this.picked); this.go('picked');
    });
    const say = () => { go.textContent = `March with the ${FACTION[this.picked ?? ARMY_CARDS[0].id].name}`; };
    say();
    return this.artPick<FactionId>({
      kind: 'army', title: 'Choose your army', sub: 'Choose your army for this match. Take your time.', label: 'Armies',
      items: ARMY_CARDS.map((a) => ({ id: a.id, short: a.name, role: a.trait, aria: `${a.name}, ${a.trait}: ${a.detail}` })),
      picked: () => this.picked, pick: (id) => { this.picked = id; say(); }, go, extra: btn('Not yet', 'text-btn', () => this.go('cancel')),
      words: (id) => {
        const a = ARMY_CARDS.find((x) => x.id === id)!;
        const named = h('span', 'kit-head'); named.append(h('b', 'name', a.name), h('small', 'role', a.trait));
        return [named, h('p', 'detail army-detail', a.detail)];
      },
    });
  }

  // ---- pause
  private pauseCard() {
    const s = this.sim();
    const card = h('section', 'card paused'); card.setAttribute('aria-label', 'Paused');
    const online = this.hooks.net.online();
    const when = h('p', 'sub'); when.append(sym('timer'), h('span', '', online ? `${mmss(s.time)} in. The match goes on while this is open.` : `${mmss(s.time)} into the match.`));
    // your hero's banner heads the card, as the end card opens on its picture of the table (2026-10-31 art cards: one
    // card language, a picture at the top of every card that has one)
    // the sim's, not the pick's: a corner taken from a bot (Jump in) has a hero nobody picked here (Flash F4)
    const hero = s.players[this.me()]?.hero?.id ?? null;
    if (hero) { const art = h('div', 'card-band'); art.setAttribute('aria-hidden', 'true'); const img = h('img'); img.sizes = '(max-width: 420px) 100vw, 380px'; img.srcset = bannerSrcset(hero); img.alt = ''; img.decoding = 'async'; art.append(img); card.append(art); card.classList.add('has-band'); }
    card.append(ribbon('Paused'), when);
    const acts = h('div', 'stack');
    const resume = btn(online ? 'Back to the match' : 'Resume', 'plaque big primary', () => this.go('resume')); resume.dataset.first = '';
    acts.append(resume);
    if (s.players[this.me()]?.hero) acts.append(btn('Replay the guide', 'plaque big', () => { this.hooks.replay(); this.go('resume'); }));
    acts.append(btn('Settings', 'plaque big', () => { this.panel = 'settings'; this.draw(); }), online ? btn('Leave the match', 'text-btn', () => this.go('title')) : btn('Quit to title', 'text-btn', () => this.go('title')));
    card.append(acts);
    return card;
  }

  // ---- the end card: who won, and every player's match in numbers
  private endCard() {
    const s = this.sim(), me = this.me(), w = endWords(s, me);
    const card = h('section', `card end ${w.tone}`); card.setAttribute('aria-label', w.title);
    if (s.winner >= 0) card.style.setProperty('--team', TEAM[s.winner]);
    const art = h('div', 'end-art'); art.setAttribute('role', 'img');
    art.setAttribute('aria-label', w.tone === 'lost' ? 'A fallen fort with a white flag' : 'A gold trophy on the table');
    card.append(art, ribbon(w.title, 'big-title'), h('p', 'sub', w.line));
    const grid = h('div', 'stats'); grid.setAttribute('role', 'table'); grid.setAttribute('aria-label', 'Match stats');
    const head = h('div', 'st-row st-head'); head.setAttribute('role', 'row');
    for (const [t, icon] of [['Player', ''], ['Kills', 'attack'], ['Gold', 'gold'], ['Base', 'base']]) {
      const c = h('span'); if (icon) c.append(sym(icon)); c.append(t); c.setAttribute('role', 'columnheader'); head.append(c);
    }
    grid.append(head);
    for (const r of endRows(s, me, this.fellAt)) {
      const row = h('div', 'st-row' + (r.you ? ' you' : '') + (r.p === s.winner ? ' won' : '') + (r.standing ? '' : ' fell'));
      row.setAttribute('role', 'row'); row.style.setProperty('--team', TEAM[r.p]);
      const who = h('span', 'who'); who.append(h('i', 'dot'), h('b', '', r.name), h('small', '', `${r.army}, tier ${r.tier}`));
      const cells = [who, h('span', 'num', String(r.kills)), h('span', 'num', String(r.gold)), h('span', 'base', r.base)];
      cells.forEach((c) => { c.setAttribute('role', 'cell'); row.append(c); });
      grid.append(row);
    }
    card.append(grid);
    // leaving is a link, as on the pause card ("Quit to title") and the pick ("Not yet"); a boxed "Title" beside
    // "Play again" read as a second button of the same rank (Flash, design pass phase 2 F4)
    const acts = h('div', 'actions');
    acts.append(btn('Back to title', 'text-btn', () => this.go('title')));
    if (s.winner < 0) acts.append(btn('Keep watching', 'plaque', () => this.go('watch')));
    const online = this.hooks.net.online();
    const again = online ? btn('Play online again', 'plaque big primary', () => this.go('online'))
      : btn(this.mode === 'watch' ? 'Watch another' : 'Play again', 'plaque big primary', () => this.go('again'));
    again.dataset.first = '';
    acts.append(again);
    card.append(acts);
    return card;
  }

  // ---- online (2026-11-01): find or make a room, see who is in it, lead with your hero, start. The card is filled in
  // place on every room message (lobbyChanged), so it doesn't drop in again or lose focus each time someone joins.
  private lobbyPanel: 'room' | 'hero' = 'room';
  private lobbyEl: HTMLElement | null = null;
  private heroSent = false;
  private countdown = 0;
  /** Who held each corner at the last draw, and in which room: a corner that turns human between two draws stands up. */
  private wasHuman: boolean[] = [];
  private lobbyCode: string | null = null;
  /** When each corner stood up: a redraw inside the moment carries it on instead of cutting it (Flash F5). */
  private arrivedAt: number[] = [];

  /** The room changed (main.ts, on every message from it). */
  lobbyChanged() {
    const v = this.hooks.net.view();
    if (v.status !== 'lobby' && v.status !== 'playing') this.heroSent = false;
    // lead with the hero you last led with; with none saved, the room's pick for your corner stands
    const saved = savedHero();
    if (v.status === 'lobby' && v.you >= 0 && saved && v.seats[v.you]?.hero !== saved && !this.heroSent) { this.heroSent = true; this.hooks.net.hero(saved); }
    // the room ended under the hero pick: back to the room card, which says why (Flash F6)
    if (this.screen === 'lobby' && this.lobbyPanel === 'hero' && v.status !== 'lobby') { this.lobbyPanel = 'room'; this.draw(); }
    else if (this.screen === 'lobby' && this.lobbyPanel === 'room' && this.lobbyEl?.isConnected) this.fillLobby(this.lobbyEl);
    this.publish();
  }

  private lobbyCard() {
    if (this.lobbyPanel === 'hero') return this.lobbyHeroCard();
    const card = h('section', 'card lobby'); card.setAttribute('aria-label', 'Play online');
    this.lobbyEl = card; this.fillLobby(card);
    return card;
  }

  private fillLobby(card: HTMLElement) {
    const v = this.hooks.net.view(), net = this.hooks.net;
    const kept = card.contains(document.activeElement) ? (document.activeElement as HTMLElement).dataset.k : undefined;
    const typed = card.querySelector<HTMLInputElement>('[data-k="code"]')?.value ?? ''; // a code with a typo stays to fix
    if (v.status !== 'lobby' && v.status !== 'playing') { this.lobbyCode = null; this.wasHuman = []; this.arrivedAt = []; }
    clearInterval(this.countdown);
    const key = <E extends HTMLElement>(e: E, k: string) => { e.dataset.k = k; return e; };
    const parts: HTMLElement[] = [ribbon('Play online')];
    if (v.status === 'idle' || v.status === 'closed') {
      parts.push(h('p', 'sub', 'Play anyone online, or send friends a room link. Bots take the empty corners.'));
      if (v.message) { const m = h('p', 'note', v.message); m.setAttribute('role', 'alert'); parts.push(m); }
      const quick = key(btn('Quick match', 'plaque big primary', () => net.quick()), 'quick'); quick.dataset.first = '';
      const form = h('form', 'join');
      const input = key(h('input'), 'code');
      Object.assign(input, { type: 'text', maxLength: 5, placeholder: 'Room code', autocomplete: 'off', spellcheck: false, name: 'room', value: typed });
      input.setAttribute('autocapitalize', 'characters'); input.setAttribute('aria-label', 'Room code');
      form.append(input, key(h('button', 'plaque', 'Join'), 'join'));
      form.addEventListener('submit', (e) => { e.preventDefault(); net.join(input.value); });
      const stack = h('div', 'stack'); stack.append(quick, key(btn('Make a room', 'plaque big', () => net.create()), 'create'), form);
      parts.push(stack, key(btn('Back to title', 'text-btn', () => this.go('title')), 'back'));
    } else if (v.status === 'finding' || v.status === 'connecting') {
      const p = h('p', 'sub', v.status === 'finding' ? 'Finding a match...' : 'Joining the room...'); p.setAttribute('role', 'status');
      parts.push(p, key(btn('Cancel', 'text-btn', () => net.cancel()), 'back'));
    } else {
      // the room as a war table (2026-11-01 /taste, Devin: "big and bold and fun"): the code stamped large with its invite
      // beside it, the four corners as team-coloured tiles with each hero's poster, Start under the thumb
      const host = v.you === v.host;
      const head = h('div', 'room-head');
      const stamp = h('p', 'room-code');
      if (v.quick) {
        const n = h('span', 'n');
        stamp.append(h('small', '', 'Quick match starts in'), n);
        const at = v.startsAt;
        const say = () => { n.textContent = at === null ? 'a moment' : `${Math.max(0, Math.ceil((at - performance.now()) / 1000))} s`; };
        say(); this.countdown = window.setInterval(say, 250);
      } else stamp.append(h('small', '', 'Room'), h('span', 'n', v.code ?? ''));
      head.append(stamp);
      if (!v.quick) {
        const copy = key(btn('Copy invite link', 'plaque big', async () => {
          const url = net.link();
          try {
            if (navigator.share && matchMedia('(pointer: coarse)').matches) await navigator.share({ title: 'Footmen Wars Online', text: `Join my room, ${v.code}`, url });
            else { await navigator.clipboard.writeText(url); copy.textContent = 'Link copied'; }
          } catch { copy.textContent = url; }
        }), 'link');
        head.append(copy);
      }
      parts.push(head);
      const sub = h('p', 'sub'); sub.setAttribute('role', 'status');
      sub.textContent = v.quick ? 'Anyone who finds a match now joins you. Bots take the empty corners.'
        : host ? 'Send friends the link, then start. Bots take the empty corners.' : 'The host starts the match. Bots take the empty corners.';
      parts.push(sub);
      const mine = v.seats[v.you]?.hero ?? lastHero();
      const list = h('ul', 'corners'); list.setAttribute('aria-label', 'Players');
      const same = this.lobbyCode === v.code;
      v.seats.forEach((st, p) => {
        const me = p === v.you, hero = me ? mine : st.hero;
        // a corner someone just took stands up (the one moment here); not on the first draw, where everyone already was
        if (same && st.human && !this.wasHuman[p]) this.arrivedAt[p] = performance.now();
        const since = st.human ? performance.now() - (this.arrivedAt[p] ?? -1e9) : 1e9;
        const li = h('li', 'corner' + (me ? ' you' : '') + (st.human ? '' : ' bot') + (since < ARRIVE_MS ? ' arrive' : ''));
        if (since < ARRIVE_MS) li.style.animationDelay = `${-Math.round(since)}ms`;
        li.style.setProperty('--team', TEAM[p]);
        // the host's tag on the tile's own corner (Flash F2: inside the words it floated over the poster, or off the tile)
        if (p === v.host && !v.quick) li.append(h('small', 'tag', 'Host'));
        if (st.human && hero) li.append(poster(hero));
        // a bot's corner shows the footman it fields (the class badge, as before an army is picked), not a hero it hasn't got
        if (!st.human) li.append(pic(unitPic('footman', null), 'bot-pic'));
        const who = h('div', 'corner-words');
        who.append(h('b', '', me ? 'You' : st.human ? `Player ${p + 1}` : 'Bot'),
          h('small', '', st.human ? (hero ? HERO[hero].short : 'Choosing a hero') : 'Open corner'));
        if (me) who.append(key(btn('Change hero', 'plaque', () => { this.hero = mine; this.lobbyPanel = 'hero'; this.draw(); }), 'hero'));
        li.append(who);
        list.append(li);
      });
      this.wasHuman = v.seats.map((st) => st.human); this.lobbyCode = v.code;
      parts.push(list);
      const acts = h('div', 'lobby-go');
      if (host) { const go = key(btn(v.quick ? 'Start now' : 'Start match', 'plaque big primary', () => net.begin()), 'start'); go.dataset.first = ''; acts.append(go); }
      acts.append(key(btn('Leave', 'text-btn', () => net.cancel()), 'back'));
      parts.push(acts);
    }
    card.replaceChildren(...parts);
    // focus stays on the same control, else the card's first: never dropped to the page (Flash F6)
    const to = (kept ? card.querySelector<HTMLElement>(`[data-k="${kept}"]`) : null)
      ?? (card.isConnected && (kept || !card.contains(document.activeElement)) ? card.querySelector<HTMLElement>('[data-first]') ?? card.querySelector<HTMLElement>('button') : null);
    to?.focus({ preventScroll: true });
  }

  /** The hero pick, inside the lobby: the same art card, and the room hears your choice. */
  private lobbyHeroCard() {
    const go = btn('', 'plaque big primary march', () => {
      if (!this.hero) return;
      try { localStorage.setItem(HERO_KEY, this.hero); } catch { /* the pick still holds for this room */ }
      this.hooks.net.hero(this.hero); this.heroSent = true;
      this.lobbyPanel = 'room'; this.draw();
    });
    const say = () => { go.textContent = `Lead with ${HERO[this.hero ?? HERO_CARDS[0].id].short}`; };
    say();
    return this.artPick<HeroId>({
      kind: 'hero', title: 'Choose your hero', sub: 'Lead any army. Grow stronger. Return after falling.', label: 'Heroes',
      items: HERO_CARDS.map((c) => ({ id: c.id, short: c.short, role: c.role, aria: `${c.name}, ${c.role}` })),
      picked: () => this.hero, pick: (id) => { this.hero = id; say(); }, go,
      extra: btn('Back', 'text-btn', () => { this.lobbyPanel = 'room'; this.draw(); }),
      words: (id) => this.heroWords(id),
    });
  }

  publish() {
    if (!this.qa) return;
    const s = this.sim(), q = ((window as any).__qa ??= {});
    q.ui = {
      screen: this.screen, panel: this.panel, mode: this.mode, picked: this.picked, hero: this.hero, settings: { ...this.settings },
      ...(this.screen === 'lobby' ? { lobby: { ...this.hooks.net.view(), panel: this.lobbyPanel } } : {}),
      ...(this.screen === 'end' ? { end: { ...endWords(s, this.me()), rows: endRows(s, this.me(), this.fellAt) } } : {}),
    };
  }
}

